/**
 * PATH: packages/bills/src/supplierPicker.ts
 * FILE_ID: BILLS-PKG-SUPPLIERPICKER-LOGIC-001
 * PURPOSE: Pure I->O decision helpers for the bill Vendor supplier-picker —
 *          what name to create, whether to offer the inline "create" action,
 *          which contact fields to carry onto a new supplier, and how to mint
 *          a Supplier.code client-side.
 * WHY: Operator complaint — "if the supplier did not come up it should just
 *      create it". The picker was link-only against an EXISTING (often empty)
 *      supplier directory. These helpers drive the new "+ Create '<vendor>'"
 *      affordance + the on-open recommendation seed, kept pure so the
 *      component stays declarative and the branching is unit-tested in
 *      isolation (mirrors supplierOption.ts).
 * FLOW:
 *   search/vendor ─▶ resolveCreateName ─▶ createName
 *   {canCreate,loading,createName,options} ─▶ shouldOfferCreate ─▶ bool
 *   createName/vendor ─▶ vendorContactForCreate ─▶ {email,abn}
 *   name(+now) ─▶ buildSupplierCode ─▶ unique-ish code
 * RELATED:
 *   - ./supplierOption.ts: sibling pure helpers (normalise + source label)
 *   - ./components/SupplierPickerField.tsx: the only consumer
 *   - feature/billsClient.ts::createSupplier: uses buildSupplierCode
 */

/** Bill vendor identity surfaced to the picker (free-text on the bill). */
export interface PickerVendor {
  name?: string | null;
  email?: string | null;
  abn?: string | null;
}

/**
 * The supplier name the inline "create" action will use: whatever the
 * operator typed in the search box, else the bill's extracted vendor name.
 * Trims both; returns '' when neither is usable (caller hides the action).
 */
export function resolveCreateName(search: string, vendorName?: string | null): string {
  const typed = (search ?? '').trim();
  if (typed) return typed;
  return (vendorName ?? '').trim();
}

/**
 * Whether to surface the "+ Create '<createName>'" affordance. False when the
 * app didn't wire `createSupplier`, while a fetch is in flight, when there's
 * nothing to name, OR when an existing supplier already carries that exact
 * name — in that last case the operator should LINK the existing one
 * (operator-driven dedupe), not mint a duplicate.
 */
export function shouldOfferCreate(params: {
  canCreate: boolean;
  loading: boolean;
  createName: string;
  options: ReadonlyArray<{ name: string }>;
}): boolean {
  const { canCreate, loading, createName } = params;
  if (!canCreate || loading) return false;
  const name = createName.trim();
  if (!name) return false;
  const lower = name.toLowerCase();
  return !params.options.some((o) => (o.name ?? '').trim().toLowerCase() === lower);
}

/**
 * Contact fields to seed a freshly-created supplier with. Only carry the
 * bill's vendor email/ABN when the name being created actually matches the
 * extracted vendor — so typing a DIFFERENT supplier name ("Boral") into the
 * search box never staples THIS bill's vendor ABN/email onto it. Returns
 * nulls otherwise (name-only create).
 */
export function vendorContactForCreate(
  createName: string,
  vendor?: PickerVendor | null,
): { email: string | null; abn: string | null } {
  const name = (createName ?? '').trim();
  const vendorName = (vendor?.name ?? '').trim();
  const matchesVendor = !!name && !!vendor && name.toLowerCase() === vendorName.toLowerCase();
  if (!matchesVendor) return { email: null, abn: null };
  return {
    email: (vendor!.email ?? '').trim() || null,
    abn: (vendor!.abn ?? '').trim() || null,
  };
}

/**
 * Slug seed for a `Supplier.code` from a free-text name. Mirrors the backend
 * `_unique_supplier_code` seed rule (uppercase, strip non-alphanumerics, cap
 * length); falls back to 'SUP' for empty/punctuation-only names. The caller
 * appends a uniqueness suffix (see `buildSupplierCode`).
 */
export function supplierCodeSeed(name: string, maxLen = 8): string {
  const slug = (name ?? '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, maxLen);
  return slug || 'SUP';
}

/**
 * Mint a `Supplier.code` client-side (the model requires a unique-per-org
 * code, and the frontend can't query the DB to dedupe). Seed + a base36
 * time suffix keeps it readable yet collision-resistant; `now` is injectable
 * for deterministic tests. Capped at the model's 50-char ceiling via a hard
 * 12-char budget (8 seed + 4 suffix) so codes stay scannable in the UI.
 */
export function buildSupplierCode(name: string, now: number = Date.now()): string {
  const seed = supplierCodeSeed(name, 8);
  const suffix = Math.abs(Math.trunc(now)).toString(36).toUpperCase().slice(-4) || '0';
  return `${seed}${suffix}`.slice(0, 12);
}
